import { cn } from '../../utils/cn';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface TabItem {
  /** Unique value identifier for the tab */
  value: string;
  /** Display label */
  label: string;
  /** Disable this tab */
  disabled?: boolean;
  /** Optional icon rendered before the label */
  icon?: React.ReactNode;
}

interface TabsProps {
  /** Tab definitions */
  items: TabItem[];
  /** Currently active tab value (controlled) */
  value: string;
  /** Callback when a tab is selected */
  onChange: (value: string) => void;
  /** Accessible label for the tab list */
  ariaLabel?: string;
  /** ID prefix for generating aria-controls (links tab to tabpanel) */
  idPrefix?: string;
  className?: string;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function Tabs({
  items,
  value,
  onChange,
  ariaLabel = 'Tabs',
  idPrefix = 'tab',
  className,
}: TabsProps) {
  return (
    <div
      role="tablist"
      aria-label={ariaLabel}
      className={cn(
        'flex items-center gap-1 overflow-x-auto -mx-4 px-4 lg:mx-0 lg:px-0',
        'scrollbar-none',
        className,
      )}
    >
      {items.map((tab) => {
        const isActive = tab.value === value;

        return (
          <button
            key={tab.value}
            id={`${idPrefix}-${tab.value}`}
            role="tab"
            type="button"
            aria-selected={isActive}
            aria-controls={`${idPrefix}-panel-${tab.value}`}
            aria-disabled={tab.disabled || undefined}
            tabIndex={isActive ? 0 : -1}
            disabled={tab.disabled}
            onClick={() => !tab.disabled && onChange(tab.value)}
            onKeyDown={(e) => {
              const enabledItems = items.filter((t) => !t.disabled);
              const currentIdx = enabledItems.findIndex(
                (t) => t.value === tab.value,
              );
              let nextIdx = -1;

              if (e.key === 'ArrowRight') {
                nextIdx = (currentIdx + 1) % enabledItems.length;
              } else if (e.key === 'ArrowLeft') {
                nextIdx =
                  (currentIdx - 1 + enabledItems.length) %
                  enabledItems.length;
              } else if (e.key === 'Home') {
                nextIdx = 0;
              } else if (e.key === 'End') {
                nextIdx = enabledItems.length - 1;
              }

              const nextItem = nextIdx >= 0 ? enabledItems[nextIdx] : undefined;
              if (nextItem) {
                e.preventDefault();
                onChange(nextItem.value);
                // Move focus to the newly activated tab
                const tablist = e.currentTarget.parentElement;
                const buttons =
                  tablist?.querySelectorAll<HTMLButtonElement>(
                    '[role="tab"]:not([disabled])',
                  );
                buttons?.[nextIdx]?.focus();
              }
            }}
            className={cn(
              'inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap',
              'transition-all duration-200 cursor-pointer shrink-0 min-h-[var(--touch-target-min)] lg:min-h-0',
              'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-border-focus',
              isActive
                ? 'bg-accent-500 text-text-on-accent shadow-xs'
                : 'bg-bg-hover text-text-secondary hover:bg-bg-active hover:text-text-primary',
              tab.disabled && 'opacity-50 cursor-not-allowed',
            )}
          >
            {tab.icon}
            {tab.label}
          </button>
        );
      })}
    </div>
  );
}

// ---------------------------------------------------------------------------
// TabPanel -- accessible wrapper for tab content
// ---------------------------------------------------------------------------

interface TabPanelProps {
  /** Tab value this panel corresponds to */
  value: string;
  /** Currently active tab value */
  activeValue: string;
  /** ID prefix matching the Tabs component */
  idPrefix?: string;
  children: React.ReactNode;
  className?: string;
}

/**
 * Accessible tab panel container. Renders as `role="tabpanel"` with
 * `aria-labelledby` linking to its corresponding tab trigger.
 *
 * Only renders children when `value === activeValue`. Uses
 * `tabIndex={0}` so the panel is focusable for keyboard users.
 */
export function TabPanel({
  value,
  activeValue,
  idPrefix = 'tab',
  children,
  className,
}: TabPanelProps) {
  if (value !== activeValue) return null;

  return (
    <div
      id={`${idPrefix}-panel-${value}`}
      role="tabpanel"
      aria-labelledby={`${idPrefix}-${value}`}
      tabIndex={0}
      className={cn('focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-border-focus rounded-lg', className)}
    >
      {children}
    </div>
  );
}
